<template>
  <div class="device-management">
    <div v-if="isLoadingDevices" class="d-flex align-items-center justify-content-center gap-2 p-3 font-500">
      <b-spinner small></b-spinner> Loading...
    </div>
    <div v-else>
        <div v-if="devices && devices.length">
          <div class="section-header d-flex justify-content-between align-items-center">
            <div>
              <div class="text-muted text-bold">
                {{ activeDevicesCount }} active · {{ removedDevicesCount }} removed {{ lastReset ? `· last reset at ${formatDate(lastReset.resetAt)}` : '' }}
              </div>
            </div>
          </div>
          <div
              v-for="device in devices"
              :key="device.srcDeviceId"
              class="device-card"
          >
            <div class="device-row d-flex align-items-start">

              <div class="device-icon mr-3">
                <i :class="getDeviceIcon(device.platform)"></i>
              </div>

              <div class="flex-grow-1 ms-2">
                <div class="device-name">
                  <span v-if="device.deviceInfo && (device.deviceInfo.brand || device.deviceInfo.model)">

                    <span v-if="device.deviceInfo.brand">
                      {{ device.deviceInfo.brand }}
                    </span>

                    <span v-if="device.deviceInfo.model">
                      {{ device.deviceInfo.model }}
                    </span>
                  </span>
                  <span v-else>
                    {{ getDeviceName(device) }}
                  </span>

                  <span class="device-id">
                    #{{ device.srcDeviceId }}
                  </span>

                  <span v-if="device.isActive === true" class="current-badge">
                    active
                  </span>
                  <span v-else class="current-badge text-danger">
                    removed
                  </span>
                </div>

                <div class="device-meta">
                  <span>
                    {{ getPlatformName(device.platform) }}
                  </span>


                  · created at {{ formatDate(device.createdAt) }}

                  <span v-if="device && device.resetHistory && device.resetHistory.length > 0">
                    <span v-if="device.isActive === true">
                      · last reactivated at {{ formatDate(device.lastActivatedAt) }}
                    </span>
                    <span v-else>
                      · removed at {{ formatDate(getResetHistory(device)[0].resetAt) }} by {{ getResetBy(getResetHistory(device)[0]) }}
                    </span>
                  </span>

                </div>
              </div>

              <div class="device-actions">

                <b-button
                    v-if="device.resetHistory && device.resetHistory.length"
                    v-b-toggle="'device-history-' + device.srcDeviceId"
                    variant="light"
                    class="reset-button"
                >
                  <i class="fa fa-clock"></i>
                  {{ device.resetHistory.length }}
                  {{ device.resetHistory.length === 1 ? 'reset' : 'resets' }}

                  <i class="fa fa-angle-down"></i>
                </b-button>

              </div>
            </div>

            <!-- RESET HISTORY -->
            <b-collapse
                v-if="device.resetHistory && device.resetHistory.length"
                :id="'device-history-' + device.srcDeviceId"
            >
              <div class="reset-history">

                <b-table-simple
                    small
                    responsive
                    class="mb-0"
                >
                  <b-thead>
                    <b-tr>
                      <b-th>S. No.</b-th>
                      <b-th>Device ID</b-th>
                      <b-th>Activation date</b-th>
                      <b-th>Reset date</b-th>
                      <b-th>Reset by</b-th>
                    </b-tr>
                  </b-thead>

                  <b-tbody>
                    <b-tr
                        v-for="(history, index) in getResetHistory(device)"
                        :key="history.deviceId + '-' + history.resetAt"
                    >
                      <b-td>
                        {{ index + 1 }}
                      </b-td>

                      <b-td>
                        {{ history.deviceId }}
                      </b-td>

                      <b-td>
                        {{ formatDate(history.createdAt || device.createdAt) }}
                      </b-td>

                      <b-td>
                        {{ formatDate(history.resetAt) }}
                      </b-td>

                      <b-td>
                        {{ getResetBy(history) }}
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>

              </div>
            </b-collapse>
          </div>
        </div>

        <div
            v-else
            class="text-muted text-center py-4"
        >
          No devices found
        </div>

    </div>
  </div>
</template>

<script>
import {mapActions} from "vuex";

export default {
  name: 'UserDeviceHistory',

  props: {
    user: Object
  },

  data() {
    return {
      isLoadingDevices: false,
      deviceHistory: null
    }
  },

  computed: {
    devices() {
      return this.deviceHistory?.devices.sort((a, b) => {

        // Active devices first
        if (a.isActive !== b.isActive) {
          return a.isActive ? -1 : 1;
        }

        // Within the same group, newest activation first
        var dateA = new Date(a.lastActivatedAt || a.createdAt);
        var dateB = new Date(b.lastActivatedAt || b.createdAt);

        return dateB - dateA;

      }) ?? [];
    },
    activeDevicesCount() {
      return this.deviceHistory?.devices.filter(d => d.isActive).length ?? 0;
    },
    removedDevicesCount() {
      return this.deviceHistory?.devices.filter(d => !d.isActive).length ?? 0;
    },
    lastReset() {
      let latest = null;

      this.devices.forEach(function (device) {
        (device.resetHistory || []).forEach(function (reset) {
          if (!latest || new Date(reset.resetAt) > new Date(latest.resetAt)) {
            latest = reset;
          }
        });
      });

      return latest;
    }
  },

  methods: {
    ...mapActions("user", ["fetchUserDevicesWithResetHistory"]),

    getDeviceName(device) {
      let platform = (device.platform || '').toLowerCase();

      switch (platform) {
        case 'android':
          return 'Android device';

        case 'ios':
          return 'iOS/iPadOS device';

        case 'windows':
          return 'Windows device';

        case 'macos':
          return 'Mac device';

        case 'desktop_web':
          return 'Desktop browser';

        case 'mobile_web':
          return 'Mobile browser';

        default:
          return 'Unknown device';
      }
    },

    getPlatformName(platform) {
      switch ((platform || '').toLowerCase()) {
        case 'android':
          return 'Android app';

        case 'ios':
          return 'iOS app';

        case 'windows':
          return 'Windows app';

        case 'desktop_web':
          return 'Desktop Browser';

        case 'mobile_web':
          return 'Mobile Browser';

        default:
          return platform || '';
      }
    },

    getDeviceIcon(platform) {
      switch ((platform || '').toLowerCase()) {
        case 'android':
          return 'fa fa-android';
        case 'ios':
        case 'macos':
          return 'fa fa-apple';

        case 'windows':
          return 'fa fa-windows';
        case 'desktop_web':
        case 'mobile_web':
          return 'fa fa-globe';
        default:
          return 'fa fa-desktop';
      }
    },

    getResetHistory(device) {
      return (device.resetHistory || []).slice().sort(function (a, b) {
        return new Date(b.resetAt) - new Date(a.resetAt);
      });
    },

    getResetBy(history) {
      if (history.resetBySelf) {
         const selfName = [
          history.resetByFirstName,
          history.resetByLastName
        ].filter(Boolean).join(' ');

        return selfName
            ? selfName + ' (self)'
            : 'Self';
      }

      var name = [
        history.resetByFirstName,
        history.resetByLastName
      ].filter(Boolean).join(' ');

      return history.resetByEmail ? `${name} (${history.resetByEmail})` : 'System';
    },

    formatDate(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(value);

      if (isNaN(date.getTime())) {
        return '-';
      }

      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];

      const day = String(date.getDate()).padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      let hour = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');

      const ampm = hour >= 12 ? 'pm' : 'am';

      hour = hour % 12;
      hour = hour || 12;

      return `${day}-${month}-${year} ${String(hour).padStart(2, '0')}:${minutes}${ampm}`;
    },
  },

  async created() {
    try {
      this.isLoadingDevices = true;
      this.deviceHistory = await this.fetchUserDevicesWithResetHistory(this.user.id);
    } catch (err) {
      console.error(err);
      this.deviceHistory = [];
    } finally {
      this.isLoadingDevices = false;
    }

    console.log(this.activeDevicesCount, 'activeDevicesCount');

    if(this.activeDevicesCount === 0) {
      this.$emit("disable");
    }

  }
};
</script>

<style scoped>
.device-management {
  color: #172033;
}

.device-section {
  border: 1px solid #dfe4ea;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
}

.section-header h4 {
  font-size: 23px;
  font-weight: 600;
  color: #172033;
}

.info-icon {
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border: 1px solid #9da6b3;
  border-radius: 50%;
  font-size: 12px;
  color: #8b95a3;
  vertical-align: 2px;
}

.header-button {
  border: 1px solid #dce1e7;
  background: #fff;
  color: #172033;
  border-radius: 14px;
  padding: 9px 17px;
}

.device-card {
  margin: 14px 0 0 0;
  border: 1px solid #dfe4ea;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
}

.device-row {
  padding: 15px 20px;
}

.device-icon {
  width: 30px;
  text-align: center;
  color: #647185;
  font-size: 22px;
}

.device-name {
  font-size: 1.1rem;
  font-weight: 500;
}

.device-id {
  margin-left: 5px;
  color: #737e8e;
  font-size: 15px;
}

.current-badge {
  margin-left: 8px;
  color: #42b878;
  font-size: 14px;
}

.device-meta {
  color: #778294;
  font-size: 0.9rem;
}

.device-actions {
  display: flex;
  align-items: center;
  margin-left: 15px;
}

.reset-button {
  border: 1px solid #e0e4e9;
  border-radius: 20px;
  background: #fff;
  color: #778294;
  padding: 7px 13px;
  min-width: 120px;
}

.reset-icon {
  margin-right: 5px;
  font-size: 18px;
}

.delete-button {
  margin-left: 10px;
  color: #ef5350;
  font-size: 18px;
}

.reset-history {
  border-top: 1px solid #e1e6eb;
  background: #fbfcfd;
  padding: 0 15px;
}

.reset-history table {
  color: #273246;
}

.reset-history thead th {
  border-top: 0;
  color: #6f7b8c;
  font-weight: 500;
  padding-top: 16px;
  padding-bottom: 16px;
}

.reset-history tbody td {
  padding-top: 15px;
  padding-bottom: 15px;
}

@media (max-width: 767px) {
  .section-header {
    padding: 20px;
  }

  .section-header .header-button {
    padding: 7px 10px;
  }

  .device-card {
    margin-left: 15px;
    margin-right: 15px;
  }

  .device-row {
    align-items: flex-start !important;
  }

  .device-actions {
    flex-direction: column;
  }

  .delete-button {
    margin-left: 0;
  }
}
</style>